import React, { FC, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Divider from '@material-ui/core/Divider'
import Button from 'UI/Button/ButtonWithPreloader'
import ModalComponent from 'UI/Modal'
import ReactInputMask from 'react-input-mask'
import { useSnackbar } from 'notistack'
import { ISO_ALREADY_ON_YOUR_LIST, SOMETHING_WENT_WRONG } from 'constants/errors'
import { useIsoSearch, useAddExistingIso } from 'hooks/useIsoSearch'
import { IIsoExistingDto } from 'typescript/interfaces/iso'
import { useFormik } from 'formik'
import { schemaSearchExistingIso } from './schema'

interface Props {
  open: boolean
  onClose: (refresh?: boolean) => void
}

const SearchExistingISOModal: FC<Props> = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { search, results, isLoading, reset } = useIsoSearch(() =>
    enqueueSnackbar(SOMETHING_WENT_WRONG)
  )

  const { addIso, isLoading: isLoadingAdd } = useAddExistingIso(
    () => {
      enqueueSnackbar('ISO successfully added')
      onClose(true)
    },
    err => {
      if (
        err.response &&
        (err.response.data as any).description === 'ISO_ALREADY_ON_YOUR_LIST'
      ) {
        enqueueSnackbar(ISO_ALREADY_ON_YOUR_LIST)
      } else {
        enqueueSnackbar(SOMETHING_WENT_WRONG)
      }
    }
  )

  const {
    handleSubmit,
    values,
    touched,
    errors,
    handleChange,
    isValid,
    dirty,
    resetForm,
  } = useFormik({
    initialValues: { firstName: '', lastName: '', phone: '', email: '' },
    validationSchema: schemaSearchExistingIso,
    onSubmit: vals => {
      search(vals)
    },
  })

  useEffect(() => {
    if (!open) {
      resetForm()
      reset()
    }
  }, [open, resetForm, reset])

  const handleClose = () => {
    onClose()
    reset()
    resetForm()
  }

  const handleAddIso = (iso: IIsoExistingDto) => {
    addIso({
      firstName: iso.firstName,
      lastName: iso.lastName,
      phone: iso.phone,
      email: iso.email,
    })
  }

  return (
    <ModalComponent open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <Box minWidth={480} maxWidth={700}>
          <Box mb="1.5rem">
            <Typography variant="h3" color="textSecondary">
              Search Existing ISO
            </Typography>
          </Box>
          <Box display="grid" gridGap="1.5rem">
            <TextField
              label="First Name"
              name="firstName"
              value={values.firstName}
              onChange={handleChange}
              required
              fullWidth
              variant="outlined"
              size="small"
              error={Boolean(touched.firstName && errors.firstName)}
              helperText={touched.firstName && (errors.firstName as string)}
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={values.lastName}
              onChange={handleChange}
              required
              fullWidth
              variant="outlined"
              size="small"
              error={Boolean(touched.lastName && errors.lastName)}
              helperText={touched.lastName && (errors.lastName as string)}
            />
            <ReactInputMask
              name="phone"
              mask="(999)-999-9999"
              value={values.phone}
              onChange={handleChange}
            >
              {(props: any) => (
                <TextField
                  {...props}
                  label="Phone"
                  required
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={Boolean(touched.phone && errors.phone)}
                  helperText={touched.phone && (errors.phone as string)}
                />
              )}
            </ReactInputMask>
            <TextField
              label="Email"
              name="email"
              value={values.email}
              onChange={handleChange}
              required
              fullWidth
              variant="outlined"
              size="small"
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && (errors.email as string)}
            />
            <Box display="flex" justifyContent="flex-end" mt="0.5rem">
              <Button
                disabled={!dirty || !isValid}
                loading={isLoading}
                color="primary"
                variant="contained"
                type="submit"
              >
                Search
              </Button>
              <Box ml="1rem">
                <Button onClick={handleClose} color="primary" variant="outlined">
                  Close
                </Button>
              </Box>
            </Box>
          </Box>
          <Divider style={{ margin: '1.5rem 0' }} />
          <Box>
            <Typography variant="h4" color="textSecondary" gutterBottom>
              Found ISO
            </Typography>
            {results === undefined ? (
              <Typography color="textSecondary">Enter data and press Search</Typography>
            ) : results === null || results.length === 0 ? (
              <Typography color="textSecondary">No ISO found</Typography>
            ) : (
              <Box display="grid" gridRowGap="1rem">
                {results.map((item: IIsoExistingDto, index: number) => (
                  <Box
                    key={`${item.email}-${item.firstName}-${item.lastName}`}
                    p="1rem"
                    border="1px solid #E6E9F0"
                    borderRadius={4}
                  >
                    <Box display="grid" gridRowGap="0.5rem">
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">First Name</Typography>
                        <Typography>{item.firstName}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Last Name</Typography>
                        <Typography>{item.lastName}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Phone</Typography>
                        <Typography>{item.phone}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Mobile</Typography>
                        <Typography>{item.mobile}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Email</Typography>
                        <Typography>{item.email}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Fax</Typography>
                        <Typography>{item.fax}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="flex-end" mt="0.5rem">
                        <Button
                          onClick={() => handleAddIso(item)}
                          loading={isLoadingAdd}
                          color="primary"
                          variant="contained"
                        >
                          Add ISO
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </form>
    </ModalComponent>
  )
}

export default SearchExistingISOModal
